export const ROSTER_IMPORT_MAX_BYTES = 5 * 1024 * 1024
export const ROSTER_IMPORT_MAX_ROWS = 1000
export const ROSTER_IMPORT_MAX_COLUMNS = 100
export const ROSTER_IMPORT_MAX_CELL_LENGTH = 4000

export const ROSTER_IMPORT_FIELDS = [
  {
    key: 'date',
    label: '勤務日',
    aliases: ['勤務日', '日付', '対象日', '年月日', '勤務年月日', 'date', 'workdate', 'work_date', 'day']
  },
  {
    key: 'start',
    label: '開始時刻',
    aliases: ['開始時刻', '開始', '出勤時刻', '出勤', '始業時刻', '始業', 'clockin', 'clock_in', 'starttime', 'start_time']
  },
  {
    key: 'end',
    label: '終了時刻',
    aliases: ['終了時刻', '終了', '退勤時刻', '退勤', '終業時刻', '終業', 'clockout', 'clock_out', 'endtime', 'end_time']
  },
  {
    key: 'rest',
    label: '休憩時間',
    aliases: ['休憩時間', '休憩合計', '休憩', '休憩分', '通常休憩', 'breaktime', 'break_time', 'break', 'resttime', 'rest_time']
  },
  {
    key: 'nightRest',
    label: '夜勤休憩',
    aliases: ['夜勤休憩', '夜間休憩', '深夜休憩', '夜休憩', 'nightbreak', 'night_break', 'nightrest', 'night_rest']
  },
  {
    key: 'attendance1',
    label: '勤怠区分／勤怠1',
    aliases: ['勤怠区分', '勤務区分', '勤務種別', '勤怠1', '勤怠', '区分', 'attendance', 'attendance1', 'attendance_1', 'worktype', 'work_type']
  },
  {
    key: 'attendance2',
    label: '勤怠2',
    aliases: ['勤怠2', '午後区分', '後半区分', 'attendance2', 'attendance_2', 'pmtype', 'pm_type']
  },
  {
    key: 'project',
    label: 'プロジェクト',
    aliases: ['プロジェクト', 'プロジェクト名', '案件', '案件名', '現場', '勤務先', '作業場所', 'project', 'projectname', 'project_name']
  },
  {
    key: 'remarks',
    label: '備考・作業内容',
    aliases: ['備考作業内容', '備考・作業内容', '備考', '作業内容', '業務内容', 'コメント', '摘要', 'remarks', 'memo', 'note', 'description']
  }
]

const DELIMITERS = ['\t', ',', ';', '|']
const DELIMITER_LABELS = {
  '\t': 'タブ',
  ',': 'カンマ',
  ';': 'セミコロン',
  '|': '縦線'
}

export function delimiterLabel (delimiter) {
  return DELIMITER_LABELS[delimiter] || delimiter
}

export function normalizeText (value) {
  return String(value == null ? '' : value)
    .normalize('NFKC')
    .replace(/^\uFEFF/, '')
    .trim()
}

export function normalizeComparable (value) {
  return normalizeText(value)
    .toLowerCase()
    .replace(/[\s\u3000]/g, '')
    .replace(/[()（）\[\]【】「」『』<>＜＞]/g, '')
    .replace(/[・･／/\\＿_\-―ー:：.．,，]/g, '')
}

function binaryControlRatio (text) {
  if (!text) return 0
  let controls = 0
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i)
    if (code === 9 || code === 10 || code === 13) continue
    if ((code >= 0 && code < 32) || code === 127) controls += 1
  }
  return controls / text.length
}

function decodeWith (bytes, encoding, fatal) {
  const decoder = new TextDecoder(encoding, { fatal: Boolean(fatal) })
  return decoder.decode(bytes)
}

function looksLikeUtf16 (bytes) {
  const sample = bytes.subarray(0, Math.min(bytes.length, 200))
  if (sample.length < 4) return null
  let evenZero = 0
  let oddZero = 0
  for (let i = 0; i < sample.length; i += 1) {
    if (sample[i] !== 0) continue
    if (i % 2 === 0) evenZero += 1
    else oddZero += 1
  }
  const half = sample.length / 2
  if (oddZero / half > 0.25) return 'utf-16le'
  if (evenZero / half > 0.25) return 'utf-16be'
  return null
}

export function decodeRosterFile (arrayBuffer, requestedEncoding = 'auto') {
  if (!(arrayBuffer instanceof ArrayBuffer)) {
    throw new Error('ファイルを読み取れませんでした。')
  }
  if (arrayBuffer.byteLength === 0) {
    throw new Error('ファイルが空です。')
  }
  if (arrayBuffer.byteLength > ROSTER_IMPORT_MAX_BYTES) {
    throw new Error('ファイルサイズは5MB以下にしてください。')
  }

  const bytes = new Uint8Array(arrayBuffer)
  let encoding = requestedEncoding
  let offset = 0
  const warnings = []

  if (requestedEncoding === 'auto') {
    if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
      encoding = 'utf-8'
      offset = 3
    } else if (bytes[0] === 0xFF && bytes[1] === 0xFE) {
      encoding = 'utf-16le'
      offset = 2
    } else if (bytes[0] === 0xFE && bytes[1] === 0xFF) {
      encoding = 'utf-16be'
      offset = 2
    } else {
      const utf16 = looksLikeUtf16(bytes)
      if (utf16) {
        encoding = utf16
      } else {
        try {
          decodeWith(bytes, 'utf-8', true)
          encoding = 'utf-8'
        } catch (error) {
          encoding = 'shift_jis'
          warnings.push('UTF-8として読めなかったため、Shift_JISとして読み込みました。')
        }
      }
    }
  }

  const supported = ['utf-8', 'shift_jis', 'utf-16le', 'utf-16be']
  if (!supported.includes(encoding)) {
    throw new Error('文字コードの指定が不正です。')
  }

  let text
  try {
    text = decodeWith(bytes.subarray(offset), encoding, false)
  } catch (error) {
    throw new Error('指定した文字コードでファイルを読み取れませんでした。')
  }

  text = text.replace(/^\uFEFF/, '').replace(/\u0000+$/g, '')
  if (binaryControlRatio(text) > 0.01) {
    throw new Error('Excelブックなどのバイナリファイルは直接読み込めません。CSVまたはTSVで保存してください。')
  }

  return { text, encoding, warnings }
}

export function parseDelimitedText (text, delimiter) {
  if (!DELIMITERS.includes(delimiter)) {
    throw new Error('区切り文字を選択してください。')
  }

  const normalized = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const records = []
  let row = []
  let field = ''
  let inQuotes = false
  let line = 1
  let recordLine = 1

  const pushField = () => {
    if (field.length > ROSTER_IMPORT_MAX_CELL_LENGTH) {
      throw new Error(`${recordLine}行目に長すぎる項目があります。1項目は4000文字以内にしてください。`)
    }
    row.push(field)
    field = ''
    if (row.length > ROSTER_IMPORT_MAX_COLUMNS) {
      throw new Error(`${recordLine}行目の列数が多すぎます。100列以内にしてください。`)
    }
  }

  const pushRecord = () => {
    pushField()
    records.push({ cells: row, line: recordLine })
    if (records.length > ROSTER_IMPORT_MAX_ROWS) {
      throw new Error('データ行は1000行以内にしてください。')
    }
    row = []
  }

  for (let index = 0; index < normalized.length; index += 1) {
    const character = normalized[index]
    if (inQuotes) {
      if (character === '"') {
        if (normalized[index + 1] === '"') {
          field += '"'
          index += 1
        } else {
          inQuotes = false
        }
      } else {
        field += character
        if (character === '\n') line += 1
      }
      continue
    }

    if (character === '"' && field.length === 0) {
      inQuotes = true
    } else if (character === delimiter) {
      pushField()
    } else if (character === '\n') {
      pushRecord()
      line += 1
      recordLine = line
    } else {
      field += character
    }
  }

  if (inQuotes) {
    throw new Error(`${recordLine}行目のダブルクォートが閉じていません。`)
  }
  if (field.length > 0 || row.length > 0) pushRecord()

  while (records.length && isBlankRecord(records[records.length - 1])) {
    records.pop()
  }
  while (records.length && isBlankRecord(records[0])) {
    records.shift()
  }
  if (!records.length) throw new Error('取込可能な行がありません。')
  return records
}

export function isBlankRecord (record) {
  return !record || !record.cells || record.cells.every(cell => normalizeText(cell) === '')
}

function delimiterScore (records) {
  const counts = records
    .filter(record => !isBlankRecord(record))
    .slice(0, 20)
    .map(record => record.cells.length)
  if (!counts.length) return -Infinity
  const frequency = new Map()
  counts.forEach(count => frequency.set(count, (frequency.get(count) || 0) + 1))
  let mode = 0
  let modeFrequency = 0
  frequency.forEach((count, columnCount) => {
    if (count > modeFrequency || (count === modeFrequency && columnCount > mode)) {
      mode = columnCount
      modeFrequency = count
    }
  })
  if (mode <= 1) return -1000
  const variance = counts.reduce((sum, count) => sum + Math.abs(count - mode), 0)
  return modeFrequency * 100 + mode * 5 - variance * 10
}

export function detectDelimiter (text) {
  let best = null
  DELIMITERS.forEach((delimiter, priority) => {
    try {
      const records = parseDelimitedText(text, delimiter)
      const score = delimiterScore(records) - priority * 0.01
      if (!best || score > best.score) best = { delimiter, score }
    } catch (error) {
      // Try the next delimiter.
    }
  })
  if (!best || best.score < 0) {
    throw new Error('列の区切りを判定できませんでした。タブまたはカンマ区切りで保存してください。')
  }
  return best.delimiter
}

function aliasScore (header, alias) {
  const normalizedHeader = normalizeComparable(header)
  const normalizedAlias = normalizeComparable(alias)
  if (!normalizedHeader || !normalizedAlias) return 0
  if (normalizedHeader === normalizedAlias) return 1000 + normalizedAlias.length
  if (normalizedHeader.startsWith(normalizedAlias) && normalizedAlias.length >= 2) return 600 + normalizedAlias.length
  if (normalizedHeader.endsWith(normalizedAlias) && normalizedAlias.length >= 3) return 400 + normalizedAlias.length
  return 0
}

export function suggestRosterMappings (headers) {
  const candidates = []
  ROSTER_IMPORT_FIELDS.forEach(field => {
    headers.forEach((header, columnIndex) => {
      let score = 0
      field.aliases.forEach(alias => {
        score = Math.max(score, aliasScore(header, alias))
      })
      if (score > 0) candidates.push({ fieldKey: field.key, columnIndex, score })
    })
  })
  candidates.sort((a, b) => b.score - a.score)

  const mappings = {}
  const usedColumns = new Set()
  const usedFields = new Set()
  candidates.forEach(candidate => {
    if (usedColumns.has(candidate.columnIndex) || usedFields.has(candidate.fieldKey)) return
    mappings[candidate.fieldKey] = candidate.columnIndex
    usedColumns.add(candidate.columnIndex)
    usedFields.add(candidate.fieldKey)
  })
  ROSTER_IMPORT_FIELDS.forEach(field => {
    if (!Object.prototype.hasOwnProperty.call(mappings, field.key)) mappings[field.key] = null
  })
  return mappings
}

function headerCandidateScore (record) {
  if (!record || isBlankRecord(record)) return -Infinity
  const mappings = suggestRosterMappings(record.cells)
  const matchedFields = Object.entries(mappings)
    .filter(([, value]) => value != null)
    .map(([key]) => key)
  const matched = matchedFields.length
  const hasDate = mappings.date != null
  const hasTime = mappings.start != null || mappings.end != null
  const hasWorkField = hasTime || mappings.attendance1 != null || mappings.remarks != null || mappings.project != null
  const timeLike = record.cells.filter(value => parseClockTime(value, { allowOvernight: true }).value).length
  if (!matched || timeLike > Math.max(1, record.cells.length / 2)) return -Infinity
  return matched * 100 + (hasDate ? 80 : 0) + (hasTime ? 30 : 0) + (hasWorkField ? 20 : 0) - record.line * 0.01
}

export function detectHeaderRecordIndex (records) {
  let bestIndex = -1
  let bestScore = -Infinity
  records.slice(0, 30).forEach((record, index) => {
    const score = headerCandidateScore(record)
    if (score > bestScore) {
      bestScore = score
      bestIndex = index
    }
  })
  return bestScore >= 200 ? bestIndex : -1
}

export function detectHeaderRow (records) {
  return detectHeaderRecordIndex(records) >= 0
}

export function buildSourceColumns (records, hasHeader, headerRecordIndex = 0) {
  const first = hasHeader
    ? records[Number(headerRecordIndex)]
    : records.find(record => !isBlankRecord(record))
  if (!first) return []
  const relevantRecords = hasHeader
    ? records.slice(Number(headerRecordIndex))
    : records
  const count = relevantRecords.reduce((max, record) => Math.max(max, record.cells.length), 0)
  return Array.from({ length: count }, (_, index) => {
    const header = hasHeader ? normalizeText(first.cells[index]) : ''
    return {
      index,
      label: header || `列${index + 1}`,
      header
    }
  })
}

export function validateColumnMappings (mappings, rowMode) {
  const errors = []
  if (rowMode === 'date' && mappings.date == null) {
    errors.push('「勤務日」に対応する列を選択してください。')
  }
  const used = new Map()
  Object.keys(mappings).forEach(key => {
    const column = mappings[key]
    if (column == null || column === '') return
    if (used.has(String(column))) {
      errors.push(`同じ列を「${used.get(String(column))}」と「${fieldLabel(key)}」の両方へ割り当てることはできません。`)
    } else {
      used.set(String(column), fieldLabel(key))
    }
  })
  if (mappings.start == null && mappings.end == null && mappings.attendance1 == null && mappings.remarks == null && mappings.project == null) {
    errors.push('開始時刻、終了時刻、勤怠区分、プロジェクト、備考のいずれかを割り当ててください。')
  }
  return errors
}

function fieldLabel (key) {
  const definition = ROSTER_IMPORT_FIELDS.find(field => field.key === key)
  return definition ? definition.label : key
}

function pad2 (value) {
  return String(value).padStart(2, '0')
}

export function daysInMonth (year, month) {
  return new Date(Number(year), Number(month), 0).getDate()
}

function validDateParts (year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

function excelSerialToDate (serial) {
  const milliseconds = Date.UTC(1899, 11, 30) + Math.floor(serial) * 86400000
  const date = new Date(milliseconds)
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate()
  }
}

export function parseRosterDate (rawValue, selectedYear, selectedMonth) {
  let value = normalizeText(rawValue)
  if (!value) return { error: '勤務日が空です。' }
  const datePrefix = value.match(/^(\d{4}[\/\-.年]\d{1,2}[\/\-.月]\d{1,2}日?)(?:[T\s].*)?$/)
  if (datePrefix) value = datePrefix[1]
  value = value
    .replace(/[（(][月火水木金土日]曜?[）)]/g, '')
    .replace(/[（(][^）)]*[）)]$/g, '')
    .trim()

  let year
  let month
  let day

  if (/^\d{8}$/.test(value)) {
    year = Number(value.slice(0, 4))
    month = Number(value.slice(4, 6))
    day = Number(value.slice(6, 8))
  } else if (/^\d{4}$/.test(value) && Number(value.slice(0, 2)) <= 12) {
    year = Number(selectedYear)
    month = Number(value.slice(0, 2))
    day = Number(value.slice(2, 4))
  } else if (/^\d+(?:\.\d+)?$/.test(value) && Number(value) >= 20000 && Number(value) <= 80000) {
    const converted = excelSerialToDate(Number(value))
    year = converted.year
    month = converted.month
    day = converted.day
  } else {
    const normalized = value
      .replace(/年/g, '/')
      .replace(/月/g, '/')
      .replace(/日/g, '')
      .replace(/[.\-]/g, '/')
      .replace(/\s+/g, '')
    const parts = normalized.split('/').filter(Boolean)
    if (parts.length === 3) {
      if (parts[2].length === 4 && Number(parts[2]) === Number(selectedYear)) {
        year = Number(parts[2])
        month = Number(parts[0])
        day = Number(parts[1])
      } else {
        year = Number(parts[0])
        month = Number(parts[1])
        day = Number(parts[2])
      }
    } else if (parts.length === 2) {
      year = Number(selectedYear)
      month = Number(parts[0])
      day = Number(parts[1])
    } else if (parts.length === 1 && /^\d{1,2}$/.test(parts[0])) {
      year = Number(selectedYear)
      month = Number(selectedMonth)
      day = Number(parts[0])
    }
  }

  if (![year, month, day].every(Number.isFinite) || !validDateParts(year, month, day)) {
    return { error: `勤務日「${normalizeText(rawValue)}」を日付として解釈できません。` }
  }
  if (year !== Number(selectedYear) || month !== Number(selectedMonth)) {
    return { error: `勤務日${year}/${month}/${day}は表示中の${selectedYear}年${selectedMonth}月ではありません。` }
  }

  return {
    value: `${year}-${pad2(month)}-${pad2(day)}`,
    index: day - 1,
    day
  }
}

function parseAmPm (value) {
  const lower = value.toLowerCase()
  const isPm = /午後|\bpm\b/.test(lower)
  const isAm = /午前|\bam\b/.test(lower)
  return {
    isPm,
    isAm,
    value: lower.replace(/午前|午後|\bam\b|\bpm\b/g, '').trim()
  }
}

export function parseClockTime (rawValue, options = {}) {
  const allowOvernight = Boolean(options.allowOvernight)
  let value = normalizeText(rawValue)
  if (!value) return { value: '' }

  const ampm = parseAmPm(value)
  value = ampm.value
  const dateTimeMatch = value.match(/(?:^|\s)(\d{1,2}:\d{2}(?::\d{2})?)\s*$/)
  if (dateTimeMatch) value = dateTimeMatch[1]

  const warnings = []
  let hour
  let minute
  let second = 0

  if (/^0(?:\.\d+)$/.test(value)) {
    const totalMinutes = Math.round(Number(value) * 24 * 60)
    hour = Math.floor(totalMinutes / 60)
    minute = totalMinutes % 60
    warnings.push('Excelの時刻数値を時刻へ変換しました。')
  } else if (/^\d{3,4}$/.test(value)) {
    const digits = value.padStart(4, '0')
    hour = Number(digits.slice(0, 2))
    minute = Number(digits.slice(2, 4))
  } else {
    const japanese = value.match(/^(\d{1,2})時(?:(\d{1,2})分?)?$/)
    const separated = value.match(/^(\d{1,2})(?:[:.])(\d{1,2})(?::(\d{1,2}))?$/)
    if (japanese) {
      hour = Number(japanese[1])
      minute = Number(japanese[2] || 0)
    } else if (separated) {
      hour = Number(separated[1])
      minute = Number(separated[2])
      second = Number(separated[3] || 0)
      if (value.includes('.')) warnings.push('「.」区切りを「:」として解釈しました。')
    } else {
      return { error: `時刻「${normalizeText(rawValue)}」を解釈できません。` }
    }
  }

  if (ampm.isPm && hour < 12) hour += 12
  if (ampm.isAm && hour === 12) hour = 0
  if (minute < 0 || minute > 59 || second < 0 || second > 59) {
    return { error: `時刻「${normalizeText(rawValue)}」が範囲外です。` }
  }
  if (second !== 0) warnings.push('秒は勤務表へ保存しないため切り捨てました。')

  let overnight = false
  if (hour >= 24) {
    if (!allowOvernight || hour > 47) {
      return { error: `時刻「${normalizeText(rawValue)}」が範囲外です。` }
    }
    hour %= 24
    overnight = true
    warnings.push('24時以降の終了時刻は翌日の時刻へ変換しました。')
  }
  if (hour < 0 || hour > 23) {
    return { error: `時刻「${normalizeText(rawValue)}」が範囲外です。` }
  }

  return { value: `${pad2(hour)}:${pad2(minute)}`, overnight, warnings }
}

export function inferDurationUnit (headerValue) {
  const header = normalizeComparable(headerValue)
  if (!header) return 'auto'
  if (header.includes('分') || header.includes('minute') || header.includes('minutes') || header.endsWith('min')) return 'minutes'
  if (/\(h\)|時間単位|hour|hours/.test(normalizeText(headerValue).toLowerCase())) return 'hours'
  return 'auto'
}

export function parseDuration (rawValue, unitHint = 'auto') {
  let value = normalizeText(rawValue)
  if (!value) return { value: '', minutes: null }
  const warnings = []
  let minutes

  const colon = value.match(/^(\d{1,3}):(\d{1,2})(?::\d{1,2})?$/)
  const minuteSuffix = value.match(/^(\d+(?:\.\d+)?)\s*(?:分|min|mins|minute|minutes)$/i)
  const hourSuffix = value.match(/^(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|時間|hour|hours)$/i)

  if (colon) {
    const hour = Number(colon[1])
    const minute = Number(colon[2])
    if (minute > 59) return { error: `時間「${value}」の分が範囲外です。` }
    minutes = hour * 60 + minute
  } else if (minuteSuffix) {
    minutes = Math.round(Number(minuteSuffix[1]))
  } else if (hourSuffix) {
    minutes = Math.round(Number(hourSuffix[1]) * 60)
  } else if (/^0(?:\.\d+)$/.test(value)) {
    minutes = Math.round(Number(value) * 24 * 60)
    warnings.push('Excelの時間数値を休憩時間へ変換しました。')
  } else if (/^\d+(?:\.\d+)?$/.test(value)) {
    const numeric = Number(value)
    if (unitHint === 'minutes') {
      minutes = Math.round(numeric)
    } else if (unitHint === 'hours') {
      minutes = Math.round(numeric * 60)
    } else if (Number.isInteger(numeric) && numeric >= 15) {
      minutes = numeric
      warnings.push('単位のない数値を分として解釈しました。')
    } else {
      minutes = Math.round(numeric * 60)
      warnings.push('単位のない数値を時間として解釈しました。')
    }
  } else {
    return { error: `時間「${value}」を解釈できません。` }
  }

  if (!Number.isFinite(minutes) || minutes < 0 || minutes >= 24 * 60) {
    return { error: `時間「${value}」が範囲外です。` }
  }

  return {
    value: `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`,
    minutes,
    warnings
  }
}

export function shiftMinutes (start, end) {
  if (!start || !end) return null
  const [startHour, startMinute] = start.split(':').map(Number)
  const [endHour, endMinute] = end.split(':').map(Number)
  const startMinutes = startHour * 60 + startMinute
  let endMinutes = endHour * 60 + endMinute
  if (endMinutes <= startMinutes) endMinutes += 24 * 60
  return endMinutes - startMinutes
}

export function resolveChoice (rawValue, choices, overrides) {
  const raw = normalizeText(rawValue)
  if (!raw) return { empty: true }
  const normalized = normalizeComparable(raw)
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, normalized)) {
    const key = overrides[normalized]
    const choice = choices.find(item => String(item.key) === String(key))
    return choice ? { choice, overridden: true } : { unresolved: true, raw, normalized }
  }
  const matches = choices.filter(choice => {
    const values = [choice.label].concat(choice.aliases || [])
    return values.some(value => normalizeComparable(value) === normalized)
  })
  if (matches.length === 1) return { choice: matches[0], automatic: true }
  return { unresolved: true, raw, normalized }
}

export function collectUnresolvedValues (records, options) {
  const {
    hasHeader,
    headerRecordIndex = 0,
    mappings,
    attendanceChoices,
    attendance2Choices,
    projectOptions
  } = options
  const dataRecords = records.slice(hasHeader ? Number(headerRecordIndex) + 1 : 0)
  const attendance1 = new Map()
  const attendance2 = new Map()
  const projects = new Map()
  const projectChoices = (projectOptions || []).map(project => ({
    key: project.id,
    label: project.name,
    aliases: [String(project.id), project.name]
  }))

  dataRecords.forEach(record => {
    if (isBlankRecord(record)) return
    const collect = (mappingKey, choices, target) => {
      const column = mappings[mappingKey]
      if (column == null) return
      const raw = normalizeText(record.cells[column])
      if (!raw) return
      const resolved = resolveChoice(raw, choices, null)
      if (resolved.unresolved) target.set(resolved.normalized, raw)
    }
    collect('attendance1', attendanceChoices || [], attendance1)
    collect('attendance2', attendance2Choices || [], attendance2)
    collect('project', projectChoices, projects)
  })

  return {
    attendance1: Array.from(attendance1, ([key, raw]) => ({ key, raw })),
    attendance2: Array.from(attendance2, ([key, raw]) => ({ key, raw })),
    projects: Array.from(projects, ([key, raw]) => ({ key, raw }))
  }
}

function rawAt (record, column) {
  if (column == null || column === '') return { mapped: false, value: '' }
  return { mapped: true, value: normalizeText(record.cells[Number(column)] || '') }
}

function isSummaryValue (value) {
  const normalized = normalizeComparable(value)
  return /^(合計|総計|小計|計|total|subtotal)$/.test(normalized) || normalized.includes('合計') || normalized.includes('total')
}

function isRepeatedHeader (record, mappings) {
  const date = rawAt(record, mappings.date)
  if (!date.mapped || !date.value) return false
  const normalized = normalizeComparable(date.value)
  return ROSTER_IMPORT_FIELDS.find(field => field.key === 'date').aliases
    .some(alias => normalizeComparable(alias) === normalized)
}

function existingValue (existingRow, key) {
  const value = existingRow && existingRow[key]
  return value == null ? '' : value
}

function hasValue (value) {
  return value !== null && value !== undefined && String(value) !== ''
}

function mergedValue (existingRow, key, importedValue, mode) {
  if (importedValue === undefined) return existingValue(existingRow, key)
  if (mode === 'empty' && hasValue(existingValue(existingRow, key))) return existingValue(existingRow, key)
  return importedValue
}

function dateKey (year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`
}

export function buildRosterImportResult (options) {
  const {
    records,
    hasHeader,
    headerRecordIndex = 0,
    mappings,
    rowMode,
    selectedYear,
    selectedMonth,
    todayKey,
    overwriteMode,
    attendanceChoices,
    attendance2Choices,
    attendanceOverrides,
    attendance2Overrides,
    projectOptions,
    projectOverrides,
    existingRows,
    sourceColumns
  } = options

  const mappingErrors = validateColumnMappings(mappings, rowMode)
  if (mappingErrors.length) {
    return {
      rows: [],
      validRows: [],
      errors: mappingErrors,
      errorCount: mappingErrors.length,
      warningCount: 0,
      skippedCount: 0
    }
  }

  const dataRecords = records.slice(hasHeader ? Number(headerRecordIndex) + 1 : 0)
  const projectChoices = (projectOptions || []).map(project => ({
    key: project.id,
    label: project.name,
    aliases: [String(project.id), project.name]
  }))
  const headerByColumn = {}
  ;(sourceColumns || []).forEach(column => { headerByColumn[column.index] = column.header || column.label })

  const resultRows = []
  const overallErrors = []
  const seenIndexes = new Map()
  let sequenceDay = 0

  dataRecords.forEach(record => {
    if (isBlankRecord(record)) return
    if (isRepeatedHeader(record, mappings)) {
      resultRows.push({ sourceLine: record.line, skipped: true, status: 'skip', messages: ['途中の見出し行を読み飛ばしました。'] })
      return
    }

    const dateCell = rawAt(record, mappings.date)
    if (dateCell.mapped && isSummaryValue(dateCell.value)) {
      resultRows.push({ sourceLine: record.line, skipped: true, status: 'skip', messages: ['合計行を読み飛ばしました。'] })
      return
    }

    let parsedDate
    if (rowMode === 'sequence') {
      sequenceDay += 1
      if (sequenceDay > daysInMonth(selectedYear, selectedMonth)) {
        resultRows.push({ sourceLine: record.line, skipped: true, status: 'skip', messages: ['対象月の日数を超える行を読み飛ばしました。'] })
        return
      }
      parsedDate = {
        value: dateKey(Number(selectedYear), Number(selectedMonth), sequenceDay),
        index: sequenceDay - 1,
        day: sequenceDay
      }
    } else {
      parsedDate = parseRosterDate(dateCell.value, selectedYear, selectedMonth)
    }

    const row = {
      sourceLine: record.line,
      date: parsedDate.value || dateCell.value,
      index: parsedDate.index,
      errors: [],
      warnings: [],
      values: {},
      raw: {}
    }

    if (parsedDate.error) {
      row.errors.push(parsedDate.error)
      resultRows.push(row)
      return
    }
    if (seenIndexes.has(row.index)) {
      row.errors.push(`${parsedDate.day}日のデータが${seenIndexes.get(row.index)}行目と重複しています。`)
    } else {
      seenIndexes.set(row.index, record.line)
    }

    const existingRow = (existingRows || [])[row.index] || {}
    const startCell = rawAt(record, mappings.start)
    const endCell = rawAt(record, mappings.end)
    const restCell = rawAt(record, mappings.rest)
    const nightRestCell = rawAt(record, mappings.nightRest)
    const attendance1Cell = rawAt(record, mappings.attendance1)
    const attendance2Cell = rawAt(record, mappings.attendance2)
    const projectCell = rawAt(record, mappings.project)
    const remarksCell = rawAt(record, mappings.remarks)

    row.raw = {
      start: startCell.value,
      end: endCell.value,
      rest: restCell.value,
      nightRest: nightRestCell.value,
      attendance1: attendance1Cell.value,
      attendance2: attendance2Cell.value,
      project: projectCell.value,
      remarks: remarksCell.value
    }

    const start = startCell.value ? parseClockTime(startCell.value) : { value: '' }
    const end = endCell.value ? parseClockTime(endCell.value, { allowOvernight: true }) : { value: '' }
    const rest = restCell.value
      ? parseDuration(restCell.value, inferDurationUnit(headerByColumn[mappings.rest]))
      : { value: '', minutes: null }
    const nightRest = nightRestCell.value
      ? parseDuration(nightRestCell.value, inferDurationUnit(headerByColumn[mappings.nightRest]))
      : { value: '', minutes: null }

    ;[start, end, rest, nightRest].forEach(parsed => {
      if (parsed.error) row.errors.push(parsed.error)
      ;(parsed.warnings || []).forEach(message => row.warnings.push(message))
    })

    let attendance1Resolution = attendance1Cell.value
      ? resolveChoice(attendance1Cell.value, attendanceChoices || [], attendanceOverrides || {})
      : { empty: true }
    let attendance2Resolution = attendance2Cell.value
      ? resolveChoice(attendance2Cell.value, attendance2Choices || [], attendance2Overrides || {})
      : { empty: true }

    if (attendance1Resolution.unresolved) {
      row.errors.push(`勤怠区分「${attendance1Resolution.raw}」の変換先を選択してください。`)
    }
    if (attendance2Resolution.unresolved) {
      row.errors.push(`勤怠2「${attendance2Resolution.raw}」の変換先を選択してください。`)
    }

    let attendance1Value
    let attendance2Value
    let clearActual = false
    if (attendance1Resolution.choice) {
      const choice = attendance1Resolution.choice
      if (choice.keep) {
        row.warnings.push(`勤怠区分「${attendance1Cell.value}」は既存値を維持します。`)
      } else {
        attendance1Value = choice.attendance1
        attendance2Value = choice.attendance2
        row.attendanceLabel = choice.label
        clearActual = Boolean(choice.clearActual)
      }
    }
    if (attendance2Resolution.choice) {
      const choice = attendance2Resolution.choice
      if (choice.keep) {
        row.warnings.push(`勤怠2「${attendance2Cell.value}」は既存値を維持します。`)
      } else if (attendance2Value != null && choice.attendance2 != null && attendance2Value !== choice.attendance2) {
        row.errors.push('勤怠区分と勤怠2の組み合わせが矛盾しています。')
      } else {
        attendance2Value = choice.attendance2
        row.attendance2Label = choice.label
      }
    }

    if (attendance1Value === undefined && (start.value || end.value)) {
      const defaultChoice = (attendanceChoices || []).find(choice => choice.defaultForTime)
      if (defaultChoice) {
        attendance1Value = defaultChoice.attendance1
        row.attendanceLabel = defaultChoice.label
        row.warnings.push('時刻が入力されているため、勤怠区分を「常勤」として扱います。')
      }
    }

    let projectValue
    if (projectCell.value) {
      const projectResolution = resolveChoice(projectCell.value, projectChoices, projectOverrides || {})
      if (projectResolution.unresolved) {
        row.warnings.push(`プロジェクト「${projectCell.value}」が見つからないため、現在の選択を維持します。`)
      } else if (projectResolution.choice && !projectResolution.choice.keep) {
        projectValue = Number(projectResolution.choice.key)
        row.projectLabel = projectResolution.choice.label
      }
    }

    if (startCell.mapped && startCell.value) row.values.start = start.value
    if (endCell.mapped && endCell.value) row.values.end = end.value
    if (restCell.mapped && restCell.value) row.values.rest = rest.value
    if (nightRestCell.mapped && nightRestCell.value) row.values.nightRest = nightRest.value
    if (attendance1Value !== undefined) row.values.attendance1 = attendance1Value
    if (attendance2Value !== undefined) row.values.attendance2 = attendance2Value
    if (projectValue !== undefined) row.values.projectId = projectValue
    if (remarksCell.mapped && remarksCell.value) row.values.remarks = remarksCell.value

    const attendance1WillApply = attendance1Value !== undefined &&
      !(overwriteMode === 'empty' && hasValue(existingValue(existingRow, 'attendance1')))
    if (attendance1Value !== undefined && !attendance1WillApply) {
      delete row.values.attendance1
      if (!attendance2Cell.value) delete row.values.attendance2
      clearActual = false
      row.warnings.push('勤怠区分は入力済みのため、既存値を維持します。')
    }

    if (clearActual && attendance1WillApply) {
      const existingActual = ['start', 'end', 'rest', 'nightRest']
        .some(key => hasValue(existingValue(existingRow, key)))
      if (overwriteMode === 'empty' && existingActual) {
        row.errors.push('終日の休暇・休日区分へ変更するには既存の時刻を空欄にする必要があります。「ファイルの値で上書き」を選択してください。')
      } else {
        const hadActual = Boolean(start.value || end.value || rest.value || nightRest.value)
        row.values.start = ''
        row.values.end = ''
        row.values.rest = ''
        row.values.nightRest = ''
        if (hadActual) row.warnings.push('終日の休暇・休日区分のため、時刻と休憩は空欄として取り込みます。')
      }
    }

    const finalStart = mergedValue(existingRow, 'start', row.values.start, overwriteMode)
    const finalEnd = mergedValue(existingRow, 'end', row.values.end, overwriteMode)
    const finalRest = mergedValue(existingRow, 'rest', row.values.rest, overwriteMode)
    const finalNightRest = mergedValue(existingRow, 'nightRest', row.values.nightRest, overwriteMode)
    const finalAttendance1 = mergedValue(existingRow, 'attendance1', row.values.attendance1, overwriteMode)
    const finalAttendance2 = mergedValue(existingRow, 'attendance2', row.values.attendance2, overwriteMode)

    if ((finalStart && !finalEnd) || (!finalStart && finalEnd)) {
      row.errors.push('開始時刻と終了時刻は両方入力してください。')
    }

    const actualImported = Boolean(start.value || end.value || rest.value || nightRest.value)
    if (row.date > todayKey && actualImported) {
      row.errors.push('未来日の勤務実績は取り込めません。')
    }

    if (finalStart && finalEnd) {
      if (finalStart === finalEnd) {
        row.errors.push('開始時刻と終了時刻を同じにすることはできません。')
      } else {
        const worked = shiftMinutes(finalStart, finalEnd)
        const regularBreak = finalRest ? parseDuration(finalRest).minutes : 0
        const nightBreak = finalNightRest ? parseDuration(finalNightRest).minutes : 0
        if (worked != null && regularBreak + nightBreak >= worked) {
          row.errors.push('休憩時間の合計は開始時刻から終了時刻までの時間より短くしてください。')
        }
      }
    } else if (finalRest || finalNightRest) {
      row.errors.push('休憩時間を取り込む場合は開始時刻と終了時刻も必要です。')
    }

    const selectedAttendance = (attendanceChoices || []).find(choice => choice.attendance1 === finalAttendance1)
    if (selectedAttendance && selectedAttendance.requiresAttendance2 && !finalAttendance2) {
      row.errors.push(`勤怠区分「${selectedAttendance.label}」では勤怠2が必要です。`)
    }
    if (selectedAttendance && selectedAttendance.allowedAttendance2 && finalAttendance2 && !selectedAttendance.allowedAttendance2.includes(finalAttendance2)) {
      row.errors.push(`勤怠区分「${selectedAttendance.label}」と勤怠2の組み合わせを確認してください。`)
    }
    if (selectedAttendance && !selectedAttendance.requiresAttendance2 && finalAttendance2) {
      row.warnings.push('この勤怠区分では勤怠2を使用しないため、勤怠2は空欄として取り込みます。')
      row.values.attendance2 = ''
    }

    if (!restCell.mapped || !restCell.value) {
      row.autoFillRest = Boolean(finalStart && finalEnd && !finalRest && !clearActual)
    }

    const changedKeys = Object.keys(row.values).filter(key => {
      const existingKey = key === 'projectId' ? 'projectId' : key
      if (overwriteMode === 'empty' && hasValue(existingValue(existingRow, existingKey))) return false
      return String(existingValue(existingRow, existingKey)) !== String(row.values[key] == null ? '' : row.values[key])
    })
    row.changedKeys = changedKeys
    if (!row.errors.length && !changedKeys.length && !row.autoFillRest) {
      row.warnings.push('反映する変更がない行です。')
    }

    resultRows.push(row)
  })

  const validRows = resultRows.filter(row => !row.skipped && row.errors && row.errors.length === 0 && (row.changedKeys.length || row.autoFillRest))
  const errorCount = resultRows.reduce((sum, row) => sum + (row.errors ? row.errors.length : 0), 0)
  const warningCount = resultRows.reduce((sum, row) => sum + (row.warnings ? row.warnings.length : 0), 0)
  const skippedCount = resultRows.filter(row => row.skipped).length
  if (!validRows.length && errorCount === 0) overallErrors.push('反映できるデータがありません。')

  return {
    rows: resultRows,
    validRows,
    errors: overallErrors,
    errorCount: errorCount + overallErrors.length,
    warningCount,
    skippedCount
  }
}

export function buildRosterTemplate (year, month) {
  const header = ['勤務日', '開始時刻', '終了時刻', '休憩時間', '夜勤休憩', '勤怠区分', '勤怠2', 'プロジェクト', '備考・作業内容']
  const rows = [header]
  const count = daysInMonth(year, month)
  for (let day = 1; day <= count; day += 1) {
    rows.push([dateKey(Number(year), Number(month), day), '', '', '', '', '', '', '', ''])
  }
  return '\uFEFF' + rows.map(row => row.join('\t')).join('\r\n') + '\r\n'
}
